import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient, type PaginatedResult } from './api-client';

/**
 * Staff accounts and roles (spec §29).
 *
 * `useStaffDirectory` exists as much for *other* screens as for the users page:
 * anything that assigns work to a colleague needs a list of colleagues, and
 * until this existed the inquiry screen took a raw user id typed into a text
 * box.
 */

export type UserRole =
  | 'SUPER_ADMIN'
  | 'ADMIN'
  | 'SALES_MANAGER'
  | 'SALES_AGENT'
  | 'CATALOG_MANAGER'
  | 'INVENTORY_MANAGER'
  | 'FINANCE_MANAGER'
  | 'SUPPORT_AGENT';

export type UserStatus = 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'PENDING_VERIFICATION';

export interface StaffMember {
  id: string;
  name: string;
  email: string;
  mobile: string | null;
  role: UserRole;
  status: UserStatus;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface StaffDetail extends StaffMember {
  firstName: string;
  lastName: string;
  lastLoginIp: string | null;
  /** What the guard will actually allow, per-user overrides applied. */
  effectivePermissions: string[];
  overrides: Array<{ code: string; name: string; group: string; granted: boolean }>;
}

export interface RoleSummary {
  role: UserRole;
  users: number;
  permissionCount: number;
  permissions: string[];
}

export interface StaffParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: UserRole;
  status?: UserStatus;
}

const keys = {
  all: ['admin', 'users'] as const,
  list: (params: StaffParams) => [...keys.all, 'list', params] as const,
  detail: (id: string) => [...keys.all, 'detail', id] as const,
  roles: () => [...keys.all, 'roles'] as const,
};

export function useStaff(params: StaffParams) {
  return useQuery({
    queryKey: keys.list(params),
    queryFn: () =>
      apiClient.get<PaginatedResult<StaffMember>>('/admin/users', {
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search || undefined,
          role: params.role,
          status: params.status,
        },
      }),
    placeholderData: (previous) => previous,
  });
}

/**
 * Every active colleague, for an assignment picker.
 *
 * Cached longer than a list view because the staff roster changes rarely and
 * this is fetched by several screens that are not about staff at all.
 */
export function useStaffDirectory() {
  return useQuery({
    queryKey: [...keys.all, 'directory'] as const,
    queryFn: () =>
      apiClient.get<PaginatedResult<StaffMember>>('/admin/users', {
        query: { limit: 100, status: 'ACTIVE' },
      }),
    staleTime: 5 * 60_000,
  });
}

export function useStaffMember(userId: string | null) {
  return useQuery({
    queryKey: keys.detail(userId ?? ''),
    queryFn: () => apiClient.get<StaffDetail>(`/admin/users/${userId}`),
    enabled: Boolean(userId),
  });
}

export function useRoles() {
  return useQuery({
    queryKey: keys.roles(),
    queryFn: () => apiClient.get<RoleSummary[]>('/admin/users/roles'),
  });
}

function useStaffMutation<TVariables>(mutationFn: (variables: TVariables) => Promise<unknown>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.all });
    },
  });
}

export function useSetStaffStatus() {
  return useStaffMutation(
    ({ userId, ...body }: { userId: string; status: UserStatus; reason?: string }) =>
      apiClient.patch(`/admin/users/${userId}/status`, body),
  );
}

export function useSetStaffRole() {
  return useStaffMutation(({ userId, role }: { userId: string; role: UserRole }) =>
    apiClient.patch(`/admin/users/${userId}/role`, { role }),
  );
}

export const ROLE_LABEL: Record<UserRole, string> = {
  SUPER_ADMIN: 'Super admin',
  ADMIN: 'Admin',
  SALES_MANAGER: 'Sales manager',
  SALES_AGENT: 'Sales agent',
  CATALOG_MANAGER: 'Catalogue manager',
  INVENTORY_MANAGER: 'Inventory manager',
  FINANCE_MANAGER: 'Finance manager',
  SUPPORT_AGENT: 'Support agent',
};

export const USER_STATUSES: UserStatus[] = [
  'ACTIVE',
  'INACTIVE',
  'SUSPENDED',
  'PENDING_VERIFICATION',
];
